import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    MapPinIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    TruckIcon,
    ClockIcon,
    BoltIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getActiveTrips } from "../api/liveMonitoringApi";
import type { ActiveTripsQueryParams, LiveTripStatus } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: LiveTripStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "STARTED", label: "Started" },
    { value: "DRIVER_ARRIVED", label: "Driver Arrived" },
    { value: "PICKED_UP", label: "Picked Up" },
    { value: "IN_PROGRESS", label: "In Progress" },
];

// ============================================================================
// Badges
// ============================================================================

const TripStatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        STARTED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        DRIVER_ARRIVED: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
        PICKED_UP: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        IN_PROGRESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s === "IN_PROGRESS" ? "bg-emerald-500 animate-pulse" : s === "STARTED" ? "bg-blue-500" : s === "DRIVER_ARRIVED" ? "bg-violet-500" : s === "PICKED_UP" ? "bg-cyan-500" : "bg-slate-400"}`} />
            {s.replace(/_/g, " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const LiveTripsRoute = () => {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<ActiveTripsQueryParams>({
        status: "",
    });

    const params: ActiveTripsQueryParams = {
        ...filters,
        status: filters.status || undefined,
    };

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-trips", params],
        queryFn: () => getActiveTrips(params),
        refetchInterval: 15_000,
    });

    const trips = data?.data?.trips ?? [];
    const totalActive = data?.data?.totalActiveTrips ?? 0;
    const breakdown = data?.data?.statusBreakdown ?? {};

    const filtered = search
        ? trips.filter(
            (t) =>
                t.driver?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                t.driver?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                t.passenger?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                t.passenger?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                t.pickup?.address?.toLowerCase().includes(search.toLowerCase()) ||
                t.dropoff?.address?.toLowerCase().includes(search.toLowerCase()) ||
                t.vehicle?.plateNumber?.toLowerCase().includes(search.toLowerCase())
        )
        : trips;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load active trips</h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error).message}</p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Summary */}
            <div className="flex flex-wrap gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-primary-50 p-2 dark:bg-primary-900/20">
                            <TruckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Active Trips</p>
                            <p className="text-xl font-bold text-primary-700 dark:text-primary-300">{totalActive}</p>
                        </div>
                    </div>
                </div>
                {Object.entries(breakdown).map(([status, count]) => (
                    <div key={status} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{status.replace(/_/g, " ")}</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">{count}</p>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search trips…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                    <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                </Button>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <select
                        value={filters.status ?? ""}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value as LiveTripStatus | "" }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {statusOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Trips List */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50">
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Status</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Pickup</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Dropoff</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Driver</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Passenger</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Fare</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Started</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-12 text-center">
                                        <MapPinIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No active trips</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((trip) => (
                                    <tr
                                        key={trip.tripId}
                                        onClick={() => navigate(`/live-monitoring/trips/${trip.tripId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <TripStatusBadge status={trip.status} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="max-w-[180px] truncate text-sm text-slate-900 dark:text-white">{trip.pickup?.address || "—"}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="max-w-[180px] truncate text-sm text-slate-900 dark:text-white">{trip.dropoff?.address || "—"}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="text-sm text-slate-900 dark:text-white">{trip.driver?.firstName} {trip.driver?.lastName}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">{trip.vehicle?.plateNumber}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                                            {trip.passenger?.firstName} {trip.passenger?.lastName}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                                            {formatCurrency(trip.estimatedFare, trip.currency)}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                            {formatTime(trip.startedAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        {filtered.length} trip{filtered.length !== 1 ? "s" : ""} shown · Auto-refreshing every 15s
                    </p>
                </div>
            </div>
        </div>
    );
};
